import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Outlet } from 'react-router-dom';
import ReactGA from 'react-ga4';
import './App.css';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import PropertyUpdate from './pages/PropertyUpdate';
import QRCode from './pages/QRCode';
import DigitalAds from './pages/DigitalAds';
import PropertySearches from './pages/PropertySearches';
import PropertySearchDetail from './pages/PropertySearchDetail';
import MainNav from './components/MainNav';
import { ViewScopeProvider } from './contexts/ViewScopeContext';
import ManageArticles from './pages/ManageArticles';
import AIAssistant from './pages/AIAssistant';
import AIConversations from './pages/AIConversations';
import EmailCampaigns from './pages/EmailCampaigns';
import AIVideo from './pages/AIVideo';
import PropertyAppraisals from './pages/PropertyAppraisals';
import PastNotifications from './pages/PastNotifications';
import MonthlyAIPropertyReport from './pages/features/MonthlyAIPropertyReport';
import DIYAIPropertyValuator from './pages/features/DIYAIPropertyValuator';
import AINewsArticles from './pages/features/AINewsArticles';
import AICoPilot from './pages/features/AICoPilot';
import QRCodeFlyer from './pages/features/QRCodeFlyer';
import PropertyAppraisalFeature from './pages/features/PropertyAppraisalFeature';
import SMSCampaigns from './pages/features/SMSCampaigns';
import MapProspectorFeature from './pages/features/MapProspectorFeature';
import Pricing from './pages/Pricing';
import Testimonials from './pages/Testimonials';
import Docs from './pages/Docs';
import UnsubscribeAgent from './pages/UnsubscribeAgent';
import ActivityExplorer from './pages/ActivityExplorer';
import EmailDetails from './pages/EmailDetails';
import MapProspector from './pages/MapProspector';
import Settings from './pages/Settings';
import Billing from './pages/Billing';
import Onboarding from './pages/Onboarding';
import Contacts from './pages/Contacts';
import Properties from './pages/Properties';
import Legal from './pages/Legal';
import FacebookCallback from './pages/FacebookCallback';
import ActivateTrial from './pages/ActivateTrial';
import BlogArticle from './pages/blog/BlogArticle';
import Changelog from './pages/blog/Changelog';
import BlogList from './pages/blog/BlogList';
import MarketingLayout from './pages/MarketingLayout';
import ScrollToTop from './components/ScrollToTop';
import FeedbackWidget from './components/FeedbackWidget';
import OnboardingWizard from './components/OnboardingWizard';
import {
  buildAgentInteractionEventName,
  buildAgentPageVisitEventName,
  trackAgentEvent,
} from './utils/agentEventTracking';

// Initialize GA4
ReactGA.initialize('G-W7Y0RCBV7Q'); // Replace with your GA4 Measurement ID

if (window.location.hostname === 'localhost' || window.location.hostname.includes('ngrok')) {
  window.api_host = 'https://property-app-dev--production-api-dev.modal.run';
} else {
  window.api_host = 'https://property-app--production-api.modal.run';
}

const TrackPageViews = () => {
  const location = useLocation();
  useEffect(() => {
    const page_path = location.pathname + location.search;
    ReactGA.send({ hitType: 'pageview', page: page_path });
  }, [location]);
  return null;
};

const AgentPlatformButtonTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const pageVisitEvent = buildAgentPageVisitEventName(location.pathname);
    // Defer so the effect returns immediately and we don't run axios
    // setup on the same task as the route transition.
    const id = setTimeout(() => {
      trackAgentEvent({
        eventName: pageVisitEvent,
        metadata: {
          trigger_context: {
            pathname: location.pathname,
            search: location.search,
            event_type: 'page_visit',
          },
        },
      });
    }, 0);
    return () => clearTimeout(id);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClickCapture = (event) => {
      // Capture-phase listener: runs *before* React's onClick. Keep the
      // synchronous portion as tiny as possible (just a couple of attribute
      // reads + early-exits) and defer all of the metadata building +
      // network call to a macrotask so the click is delivered to React
      // immediately. Previously the DOM walking + JSON serialisation +
      // axios.post setup all ran inline, which on heavy pages like
      // /contacts (large lists that re-render on the same click) was
      // perceived as input lag — every row button except the AI/Me toggle
      // (which optimistically updates local state) felt slow because the
      // tracker piled sync work on top of the parent re-render.
      const rawTarget = event.target;
      const normalizedTarget = rawTarget?.nodeType === Node.TEXT_NODE ? rawTarget.parentElement : rawTarget;
      const element = normalizedTarget?.closest?.('button, [role="button"], a, [data-track-click="true"], [data-track-card="true"]');
      if (!element) return;
      if (element.dataset?.trackEvent === 'false') return;
      if (element.disabled || element.getAttribute('aria-disabled') === 'true') return;

      // Snapshot the pathname/search now since location may have changed by
      // the time the deferred task runs (navigations).
      const pathname = location.pathname;
      const search = location.search;

      setTimeout(() => {
        try {
          const eventName = buildAgentInteractionEventName({ pathname, element });
          const metadata = {
            trigger_context: {
              pathname,
              search,
              section: eventName.split('.')[0],
              tab_page_modal: eventName.split('.')[1],
              field: eventName.split('.')[2],
              event: eventName.split('.')[3],
              element_text: (element.textContent || '').trim().slice(0, 120),
              element_id: element.id || null,
              element_name: element.name || null,
              element_tag: element.tagName?.toLowerCase() || null,
            },
          };
          trackAgentEvent({ eventName, metadata });
        } catch (err) {
          // Never let tracking break the page.
          // eslint-disable-next-line no-console
          console.debug('Deferred click tracking failed', err?.message || err);
        }
      }, 0);
    };

    document.addEventListener('click', onClickCapture, true);
    return () => {
      document.removeEventListener('click', onClickCapture, true);
    };
  }, [location.pathname, location.search]);

  return null;
};

// Layout with MainNav for all app pages
function AppLayout() {
  const [showWizard, setShowWizard] = useState(false);
  const [wizardAgent, setWizardAgent] = useState(null);
  const [wizardChecked, setWizardChecked] = useState(false);
  const [contactsProcessing, setContactsProcessing] = useState(false);
  const [crmProcessing, setCrmProcessing] = useState(false);
  // MainNav renders a fixed 56px top bar on mobile (<768px). The processing
  // banner is the first child of the content column, so without an offset its
  // top slides under that bar. Track the breakpoint to clear it.
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const checkAuth = () => {
      import("./utils/utils").then(({ handleTokenAndPostRequest }) => {
        handleTokenAndPostRequest(`${window.api_host}/authenticate-token-agent`)
          .then(res => {
            const data = res.data;
            if (data.has_uploaded_contacts && !data.has_contacts) {
              setContactsProcessing(true);
            }
            if (data.has_crm_integration && !data.has_contacts) {
              setCrmProcessing(true);
            }
            if (!sessionStorage.getItem("onboarding_wizard_done")) {
              if (data.has_contacts === false) {
                setWizardAgent(data.agent_details || {});
                setShowWizard(true);
              } else {
                sessionStorage.setItem("onboarding_wizard_done", "1");
              }
            }
          })
          .catch(() => {})
          .finally(() => setWizardChecked(true));
      });
    };
    checkAuth();
  }, []);

  // Listen for successful uploads to show the processing banner immediately
  useEffect(() => {
    const onUploaded = () => setContactsProcessing(true);
    window.addEventListener("contacts-uploaded", onUploaded);
    return () => window.removeEventListener("contacts-uploaded", onUploaded);
  }, []);

  // Listen for successful CRM connection to show the processing banner immediately
  useEffect(() => {
    const onCrmConnected = () => setCrmProcessing(true);
    window.addEventListener("crm-connected", onCrmConnected);
    return () => window.removeEventListener("crm-connected", onCrmConnected);
  }, []);

  // Don't render the main layout until we know whether to show the wizard
  if (!wizardChecked) {
    return (
      <div style={{ display: "flex", height: "100vh", alignItems: "center", justifyContent: "center", background: "#f6f8fa" }}>
        <div style={{
          width: 32, height: 32, border: "3px solid #e2e8f0", borderTopColor: "#10b981",
          borderRadius: "50%", animation: "spin 0.7s linear infinite",
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <ViewScopeProvider>
      {showWizard && wizardAgent && (
        <OnboardingWizard
          agentDetails={wizardAgent}
          onComplete={() => {
            setShowWizard(false);
            sessionStorage.setItem("onboarding_wizard_done", "1");
            sessionStorage.setItem("onboarding_just_finished", "1");
            window.dispatchEvent(new Event("onboarding-finished"));
          }}
        />
      )}
      <div style={{ display: "flex", height: "100vh", background: "#f6f8fa" }}>
        <MainNav />
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {(contactsProcessing || crmProcessing) && (
            <div style={{
              background: "linear-gradient(90deg, #f0fdfa 0%, #ecfdf5 50%, #f0f9ff 100%)",
              border: "none",
              borderBottom: "1px solid #a7f3d0",
              // Clear the fixed 56px mobile top bar so the banner isn't hidden.
              marginTop: isMobile ? 56 : 0,
              padding: "10px 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              fontSize: 14,
              color: "#065f46",
              fontWeight: 500,
              flexShrink: 0,
            }}>
              <span style={{ fontSize: 18 }}>{crmProcessing ? "🔗" : "✨"}</span>
              <span>
                {crmProcessing
                  ? "Your CRM is being connected \u2014 contacts are being loaded and matched to property addresses. This typically takes 24 hours."
                  : "Your contacts are being processed and address-matched — this usually takes up to 24 hours. We'll have everything ready for you soon!"}
              </span>
              <span style={{ fontSize: 18 }}>🚀</span>
            </div>
          )}
          <Outlet />
        </div>
      </div>
      <FeedbackWidget />
    </ViewScopeProvider>
  );
}

// Capture ?token= and ?t= params from any URL on the site, persist the
// resolved token to localStorage, then strip the params from the URL bar
// (so users can safely share the link). Mirrors vendorleads-app's pattern.
const TokenIngest = () => {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fullToken = params.get('token');
    const shortCode = params.get('t');
    if (!fullToken && !shortCode) return;

    const cleanUrl = () => {
      const next = new URLSearchParams(location.search);
      next.delete('token');
      next.delete('t');
      const qs = next.toString();
      navigate(`${location.pathname}${qs ? '?' + qs : ''}${location.hash || ''}`, { replace: true });
    };

    // Let listeners (e.g. the landing page's branding hook) know a param_token
    // just landed, so a first paint that fell back to the demo agency can
    // re-resolve to the real agency without needing a refresh.
    const announceToken = () => {
      try { window.dispatchEvent(new Event('param-token-ready')); } catch (_) { /* ignore */ }
    };

    if (fullToken) {
      try { localStorage.setItem('param_token', fullToken); } catch (_) { /* ignore */ }
      announceToken();
    }

    if (shortCode) {
      fetch(`${window.api_host}/resolve-sms-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: shortCode }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data && data.token) {
            try { localStorage.setItem('param_token', data.token); } catch (_) { /* ignore */ }
            announceToken();
          }
          if (data && data.campaign) {
            try { localStorage.setItem('sms_campaign', data.campaign); } catch (_) { /* ignore */ }
          }
        })
        .catch(() => {})
        .finally(cleanUrl);
    } else {
      cleanUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  return null;
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <TokenIngest />
      <TrackPageViews />
      <AgentPlatformButtonTracker />
      <Routes>
        {/* Marketing pages with shared footer */}
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<Landing />} />
          <Route path="/features/monthly-ai-property-report" element={<MonthlyAIPropertyReport />} />
          <Route path="/features/diy-ai-property-valuator" element={<DIYAIPropertyValuator />} />
          <Route path="/features/ai-news-articles" element={<AINewsArticles />} />
          <Route path="/features/ai-co-pilot" element={<AICoPilot />} />
          <Route path="/features/qr-code-flyer" element={<QRCodeFlyer />} />
          <Route path="/features/property-appraisals" element={<PropertyAppraisalFeature />} />
          <Route path="/features/sms-campaigns" element={<SMSCampaigns />} />
          <Route path="/features/map-prospector" element={<MapProspectorFeature />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/changelog" element={<Changelog />} />
        </Route>
        {/* Public pages without footer */}
        <Route path="/unsubscribe-agent" element={<UnsubscribeAgent />} />
        <Route path="/privacy-policy" element={<Legal />} />
        <Route path="/terms" element={<Legal />} />
        <Route path="/facebook/callback" element={<FacebookCallback />} />
        <Route path="/activate" element={<ActivateTrial />} />
        {/* All other pages with nav */}
        <Route element={<AppLayout />}>
          <Route path="/getting-started" element={<Onboarding />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/campaign-property-update" element={<PropertyUpdate />} />
          <Route path="/campaign-qr-code" element={<QRCode />} />
          <Route path="/campaign-digital-ads" element={<DigitalAds />} />
          <Route path="/leads-property-searches" element={<PropertySearches />} />
          <Route path="/leads-property-searches/:emailHash/:propertyId" element={<PropertySearchDetail />} />
          <Route path="/leads-property-appraisals" element={<PropertyAppraisals />} />
          <Route path="/leads-past-notifications" element={<PastNotifications />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/properties" element={<Properties />} />
          <Route path="/user-activity" element={<ActivityExplorer />} />
          <Route path="/email-details/:messageId" element={<EmailDetails />} />
          <Route path="/map-prospector" element={<MapProspector />} />
          <Route path="/ai-news-articles" element={<ManageArticles />} />
          <Route path="/ai-email-campaigns" element={<EmailCampaigns />} />
          <Route path="/ai-video" element={<AIVideo />} />
          <Route path="/ai-assistant" element={<AIAssistant />} />
          <Route path="/needs-review" element={<AIConversations mode="review" />} />
          <Route path="/ai-inbox" element={<AIConversations mode="inbox" />} />
          {/* Legacy paths — the page itself redirects to /ai-inbox. */}
          <Route path="/ai-conversations" element={<AIConversations mode="inbox" />} />
          <Route path="/ai-outbound-messages" element={<AIConversations mode="inbox" />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/billing" element={<Billing />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;